import React, { useState } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { useProjectionMonitoring } from "@/features/observability/hooks/use-projection-monitoring";
import {
    HealthStatus,
    ProjectionHealth,
} from "@/features/observability/types/projection.types";
import "@/features/observability/styles/projector-health.css";

const NEXT_RUN_INTERVAL_MS = 4 * 60 * 60 * 1000;

type Sev = "ok" | "warn" | "crit";

const sevOf = (status: HealthStatus): Sev =>
    status === "critical" ? "crit" : status === "warn" ? "warn" : "ok";

const worst = (...statuses: Sev[]): Sev =>
    statuses.includes("crit") ? "crit" : statuses.includes("warn") ? "warn" : "ok";

const dotColor: Record<Sev, string> = {
    ok: "var(--vb-success-500)",
    warn: "#E2A100",
    crit: "var(--vb-error-500)",
};

const chipLabel: Record<Sev, string> = { ok: "Bien", warn: "Atención", crit: "Mal" };

const fmtLag = (ms: number): string => {
    if (ms < 1000) return `${Math.round(ms)} ms`;
    const s = ms / 1000;
    return s >= 10 ? `${Math.round(s)} s` : `${s.toFixed(1)} s`;
};

const fmtAgo = (ts: number | null): string => {
    if (ts === null) return "—";
    const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (seconds < 60) return `hace ${seconds} s`;
    if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
    return `hace ${Math.floor(seconds / 3600)} h`;
};

const fmtIn = (ts: number): string => {
    const seconds = Math.max(0, Math.floor((ts - Date.now()) / 1000));
    if (seconds < 60) return `en ${seconds} s`;
    if (seconds < 3600) return `en ${Math.floor(seconds / 60)} min`;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return m === 0 ? `en ${h} h` : `en ${h} h ${m} min`;
};

const fmtTime = (ts: number): string => new Date(ts).toLocaleString();

const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const fmtHour = (ts: number): string => {
    const date = new Date(ts);
    const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const dayDiff = Math.round(
        (startOfDay(new Date(Date.now())) - startOfDay(date)) / 86_400_000
    );
    const dayLabel =
        dayDiff === 0
            ? "hoy"
            : dayDiff === 1
              ? "ayer"
              : date.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
    return `${dayLabel} ${time}`;
};

const fmtPct = (value: number): string => `${(value * 100).toFixed(2)}%`;

const verdictCopy: Record<
    Sev,
    { disc: string; chip: string; title: string; sub: string }
> = {
    ok: {
        disc: "check_circle",
        chip: "Todo en orden",
        title: "Los datos están al día y cuadran",
        sub: "Las tres señales en verde. Puedes confiar en los números que ves en Metrics y en el admin.",
    },
    warn: {
        disc: "warning",
        chip: "Atención",
        title: "Posible retraso en los datos",
        sub: "Alguna señal en ámbar. Los números podrían no estar completamente al día.",
    },
    crit: {
        disc: "priority_high",
        chip: "Revisar",
        title: "Los datos podrían estar incompletos o desactualizados",
        sub: "Una o más señales en rojo. No tomes decisiones críticas sobre estos números sin revisar con ingeniería.",
    },
};

type Signal = {
    key: string;
    icon: string;
    name: string;
    question: string;
    sev: Sev;
    valueNumber: string;
    valueUnit?: string;
    valueIsText?: boolean;
    phrase: string;
    detail: string;
    action?: string;
    miniValue: string;
};

const buildSignals = (health: ProjectionHealth): Signal[] => {
    const lagSev = sevOf(health.lag.status);
    const dlqSev = sevOf(health.dlq.status);
    const drift = health.drift;
    // "Never checked" is NOT "healthy": a down/never-run reconciler must not
    // produce a green verdict for data whose correctness was never validated.
    const driftSev: Sev =
        drift === null ? "warn" : drift.workspacesWithDrift > 0 ? "warn" : "ok";

    return [
        {
            key: "fresh",
            icon: "bolt",
            name: "Frescura",
            question: "¿Lo que veo es de hace segundos o de hace rato?",
            sev: lagSev,
            valueNumber: fmtLag(health.lag.p95Ms),
            phrase:
                lagSev === "ok"
                    ? "En tiempo real"
                    : lagSev === "warn"
                      ? "Ligero retraso"
                      : "El proyector va con retraso",
            detail:
                lagSev === "ok"
                    ? `El 95% de los cambios aparece en los reportes en menos de ${fmtLag(health.lag.p95Ms)}. Prácticamente en vivo.`
                    : lagSev === "warn"
                      ? `El 95% de los cambios tarda hasta ${fmtLag(health.lag.p95Ms)} en reflejarse — algo por encima de lo normal.`
                      : "Lo que ves puede tener más de un minuto de antigüedad. El proyector está rezagado.",
            action: "Avisar a ingeniería",
            miniValue: fmtLag(health.lag.p95Ms),
        },
        {
            key: "dlq",
            icon: "error",
            name: "Actualizaciones fallidas",
            question: "¿Se perdió algo por el camino?",
            sev: dlqSev,
            valueNumber: health.dlq.depth.toLocaleString(),
            valueUnit: "pendientes",
            phrase:
                dlqSev === "ok"
                    ? "Todo reflejado"
                    : dlqSev === "warn"
                      ? "Fallos recientes"
                      : "Cambios sin reflejar",
            detail:
                health.dlq.depth === 0
                    ? "No hay cambios pendientes; todo se reflejó en los reportes."
                    : `${health.dlq.depth.toLocaleString()} cambios no llegaron a los reportes. El más antiguo lleva ${fmtAgo(health.dlq.oldestPendingAt).replace("hace ", "")} sin reprocesar.`,
            action: "Limpiar resueltos",
            miniValue: `${health.dlq.depth.toLocaleString()} pdt.`,
        },
        {
            key: "drift",
            icon: "balance",
            name: "Cuadre",
            question: "¿Los totales que muestro son los reales?",
            sev: driftSev,
            valueNumber: drift === null ? "Sin datos" : `${drift.workspacesWithDrift}`,
            valueUnit: drift === null ? undefined : "workspaces",
            valueIsText: drift === null,
            phrase:
                drift === null
                    ? "Aún sin comprobar"
                    : drift.workspacesWithDrift > 0
                      ? "No cuadran"
                      : "Cuadra",
            detail:
                drift === null
                    ? "El comprobador de cuadre aún no ha corrido."
                    : drift.workspacesWithDrift === 0
                      ? "Todos los workspaces cuadran con la realidad."
                      : `${drift.workspacesWithDrift} workspace(s) muestran un conteo distinto al real. Hay un desfase acumulado.`,
            action:
                drift === null
                    ? "Esperar al reconciler (corre cada 4 h)"
                    : "Re-proyectar desde cloud-functions",
            miniValue: drift === null ? "—" : `${drift.workspacesWithDrift} ws`,
        },
    ];
};

const lagCellClass = (ms: number): string | undefined => {
    if (ms >= 60_000) return "lag-crit";
    if (ms >= 30_000) return "lag-warn";
    return undefined;
};

export function ProjectorHealthPage() {
    const { data, loading, error, lastUpdatedAt, refresh, reconcile } =
        useProjectionMonitoring();
    const { health, lag, dlq, dlqEvents, drift } = data;
    const [techOpen, setTechOpen] = useState(true);
    const [reconciling, setReconciling] = useState(false);
    const [reconcileMsg, setReconcileMsg] = useState<string | null>(null);

    const handleReconcile = async () => {
        const pendingTotal = health?.dlq.depth ?? 0;
        if (pendingTotal === 0) return;
        const ok = window.confirm(
            `Revisar ${pendingTotal} fallos pendientes contra operations-read y marcar como resueltos (borrado lógico) los que ya se reproyectaron?`
        );
        if (!ok) return;
        setReconciling(true);
        setReconcileMsg(null);
        try {
            const summary = await reconcile(1000);
            const skipped = Object.values(summary.skippedNonOperations).reduce(
                (sum, n) => sum + n,
                0
            );
            setReconcileMsg(
                `Revisados ${summary.checked} · resueltos ${summary.resolved} · siguen fallando ${summary.stillFailing}` +
                    (skipped > 0
                        ? ` · ${skipped} no-operations omitidos`
                        : "")
            );
        } catch {
            setReconcileMsg(
                "No se pudo reconciliar — revisa permisos de escritura en ES / logs."
            );
        } finally {
            setReconciling(false);
        }
    };

    const signals = health === null ? [] : buildSignals(health);
    const overall =
        signals.length === 0
            ? "ok"
            : worst(signals[0].sev, signals[1].sev, signals[2].sev);
    const verdict = verdictCopy[overall];

    const driftLastRun = health?.drift?.lastRunAt ?? null;

    const refreshButton = (
        <button type="button" className="btn" onClick={refresh} disabled={loading}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                autorenew
            </span>
            {loading ? "Actualizando…" : "Auto · 30 s"}
        </button>
    );

    return (
        <Shell title="Projector Health" subtitle="Sistema · salud de los datos">
            <div className="projector-health">
                <PageHeader
                    title="Projector Health"
                    lede="La pregunta que responde esta página: ¿Puedo confiar ahora en los números que veo en Metrics y en el admin?"
                    right={refreshButton}
                />

                <div className="ph-live">
                    <span className="live-pulse">
                        <span className="dot" /> En vivo
                    </span>
                    <span className="sep" />
                    <span>
                        Última lectura <strong>{fmtAgo(lastUpdatedAt)}</strong>
                    </span>
                    <span className="sep" />
                    <span>
                        Auto-actualiza cada <strong>30 s</strong>
                    </span>
                    {driftLastRun !== null ? (
                        <>
                            <span className="sep" />
                            <span>
                                Cuadre comprobado <strong>{fmtAgo(driftLastRun)}</strong> ·
                                próximo {fmtIn(driftLastRun + NEXT_RUN_INTERVAL_MS)}
                            </span>
                        </>
                    ) : null}
                </div>

                {health === null ? (
                    error ? (
                        <div className="ph-verdict" data-sev="crit">
                            <div className="ph-vA">
                                <span className="ph-disc">
                                    <span className="material-symbols-outlined">priority_high</span>
                                </span>
                                <div className="ph-vA__main">
                                    <div className="ph-vA__title">
                                        No se pudo cargar la salud del proyector
                                    </div>
                                    <div className="ph-vA__sub">
                                        El backend de Metrics no pudo leer los índices de
                                        proyección. Reintenta o avisa a ingeniería.
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="ph-verdict" data-sev="ok">
                            <div className="ph-vA">
                                <span className="ph-disc">
                                    <span className="material-symbols-outlined">
                                        hourglass_top
                                    </span>
                                </span>
                                <div className="ph-vA__main">
                                    <div className="ph-vA__title">Cargando…</div>
                                    <div className="ph-vA__sub">
                                        Leyendo la salud del proyector.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        <div className="ph-verdict" data-sev={overall}>
                            <div className="ph-vA">
                                <span className="ph-disc">
                                    <span className="material-symbols-outlined">
                                        {verdict.disc}
                                    </span>
                                </span>
                                <div className="ph-vA__main">
                                    <div className="ph-vA__chip">
                                        <span className="material-symbols-outlined">circle</span>
                                        {verdict.chip} · veredicto único
                                    </div>
                                    <div className="ph-vA__title">{verdict.title}</div>
                                    <div className="ph-vA__sub">{verdict.sub}</div>
                                </div>
                                <div className="ph-vA__mini">
                                    {signals.map((signal) => (
                                        <div className="ph-mini" key={signal.key}>
                                            <span
                                                className="ph-dot"
                                                style={{ background: dotColor[signal.sev] }}
                                            />
                                            {signal.name === "Actualizaciones fallidas"
                                                ? "Fallos"
                                                : signal.name}{" "}
                                            <b>{signal.miniValue}</b>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="section__hd" style={{ marginBottom: 10 }}>
                            <div className="section__title">
                                Las 3 señales
                                <span className="count">en lenguaje humano</span>
                            </div>
                            <div className="section__meta">
                                <span>
                                    Solo hay tres formas de que los datos dejen de ser confiables. El
                                    veredicto = la peor de las tres.
                                </span>
                            </div>
                        </div>

                        <div className="ph-signals">
                            {signals.map((signal) => (
                                <div className="ph-signal" data-sev={signal.sev} key={signal.key}>
                                    <div className="ph-signal__top">
                                        <div className="ph-signal__icon">
                                            <span className="material-symbols-outlined">
                                                {signal.icon}
                                            </span>
                                        </div>
                                        <div className="ph-signal__name">{signal.name}</div>
                                        <span className="ph-signal__chip">
                                            <span className="ph-dot" />
                                            {chipLabel[signal.sev]}
                                        </span>
                                    </div>
                                    <div className="ph-signal__q">{signal.question}</div>
                                    <div className="ph-signal__value">
                                        <span
                                            className={signal.valueIsText === true ? "n n--text" : "n"}
                                        >
                                            {signal.valueNumber}
                                        </span>
                                        {signal.valueUnit !== undefined ? (
                                            <span className="u">{signal.valueUnit}</span>
                                        ) : null}
                                    </div>
                                    <div className="ph-signal__phrase">{signal.phrase}</div>
                                    <div className="ph-signal__detail">{signal.detail}</div>
                                    {signal.sev !== "ok" && signal.action !== undefined ? (
                                        <div className="ph-signal__foot">
                                            <span className="ph-todo">
                                                <span className="material-symbols-outlined">
                                                    arrow_forward
                                                </span>
                                                <b>Qué hacer:</b>
                                            </span>
                                            {signal.key === "dlq" ? (
                                                <button
                                                    type="button"
                                                    className={
                                                        signal.sev === "crit"
                                                            ? "ph-signal__action ph-signal__action--danger"
                                                            : "ph-signal__action ph-signal__action--ghost"
                                                    }
                                                    onClick={handleReconcile}
                                                    disabled={reconciling}
                                                    title="Marca como resueltos (borrado lógico en ES) los fallos ya reproyectados"
                                                >
                                                    <span className="material-symbols-outlined">
                                                        cleaning_services
                                                    </span>
                                                    {reconciling ? "Limpiando…" : signal.action}
                                                </button>
                                            ) : (
                                                <span className="ph-signal__detail">
                                                    {signal.action}
                                                </span>
                                            )}
                                            {signal.key === "dlq" && reconcileMsg !== null ? (
                                                <span className="ph-signal__detail">
                                                    {reconcileMsg}
                                                </span>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>

                        <div className="ph-tech">
                            <button
                                type="button"
                                className={techOpen ? "ph-tech__bar is-open" : "ph-tech__bar"}
                                onClick={() => setTechOpen((open) => !open)}
                            >
                                <span className="material-symbols-outlined lead">terminal</span>
                                <div>
                                    <div className="ph-tech__title">
                                        Detalle técnico para ingeniería
                                    </div>
                                    <div className="ph-tech__sub">
                                        Frescura por hora · cola de fallos · comprobación de cuadre —
                                        para diagnosticar, no es lo primero que se mira.
                                    </div>
                                </div>
                                <span className="material-symbols-outlined chev">expand_more</span>
                            </button>

                            {techOpen ? (
                                <div className="ph-tech__body">
                                    <div className="ph-tech__block">
                                        <div className="ph-tech__block-hd">
                                            <div className="ph-tech__block-title">
                                                Frescura por hora
                                            </div>
                                            <div className="ph-tech__block-meta">
                                                lag p50 / p95 / p99 · últimas 24 h, por hora
                                            </div>
                                        </div>
                                        <p className="ph-intro">
                                            Cada fila es una hora. El <b>p95</b> es el “retraso máximo
                                            habitual”: el 95% de los cambios apareció en los reportes
                                            en menos de ese tiempo. En{" "}
                                            <span className="lag-warn">ámbar</span> 30–60 s, en{" "}
                                            <span className="lag-crit">rojo</span> más de 60 s.
                                        </p>
                                        <div className="ph-scroll">
                                            <table className="ph-tbl">
                                                <thead>
                                                    <tr>
                                                        <th>Hora</th>
                                                        <th className="num">p50</th>
                                                        <th className="num">p95</th>
                                                        <th className="num">p99</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lag === null || lag.buckets.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={4} className="muted">
                                                                Sin muestras en la ventana.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        [...lag.buckets]
                                                            .reverse()
                                                            .map((bucket) => (
                                                            <tr key={bucket.ts}>
                                                                <td>{fmtHour(bucket.ts)}</td>
                                                                <td className="num">
                                                                    {fmtLag(bucket.p50Ms)}
                                                                </td>
                                                                <td
                                                                    className={`num ${lagCellClass(bucket.p95Ms) ?? ""}`.trim()}
                                                                >
                                                                    {fmtLag(bucket.p95Ms)}
                                                                </td>
                                                                <td className="num">
                                                                    {fmtLag(bucket.p99Ms)}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="ph-tech__block">
                                        <div className="ph-tech__block-hd">
                                            <div className="ph-tech__block-title">
                                                Cola de fallos
                                                {dlqEvents !== null && dlqEvents.total > 0 ? (
                                                    <span className="count">{dlqEvents.total}</span>
                                                ) : null}
                                            </div>
                                            <div className="ph-tech__block-meta">
                                                cambios que no se reflejaron y esperan reintento
                                            </div>
                                        </div>
                                        {dlq !== null && dlq.byErrorCode.length > 0 ? (
                                            <p className="ph-intro">
                                                <b>Por tipo de error:</b>{" "}
                                                {dlq.byErrorCode.map((bucket, index) => (
                                                    <span key={bucket.key}>
                                                        {index > 0 ? " · " : ""}
                                                        <b>{bucket.key}</b> {bucket.count}
                                                    </span>
                                                ))}
                                            </p>
                                        ) : null}
                                        {dlqEvents === null || dlqEvents.data.length === 0 ? (
                                            <div className="ph-empty" data-sev="ok">
                                                <span className="material-symbols-outlined">
                                                    check_circle
                                                </span>
                                                <div>
                                                    <b>Todo se reflejó correctamente</b>{" "}
                                                    <span>· no hay fallos pendientes.</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="ph-scroll">
                                                <table className="ph-tbl">
                                                    <thead>
                                                        <tr>
                                                            <th>Tipo</th>
                                                            <th>Operación</th>
                                                            <th>Error</th>
                                                            <th className="num">Intentos</th>
                                                            <th className="num">Antigüedad</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dlqEvents.data.map((failure) => (
                                                            <tr
                                                                key={`${failure.operationType}-${failure.operationId}-${failure.ts}`}
                                                            >
                                                                <td className="strong">
                                                                    {failure.operationType}
                                                                </td>
                                                                <td className="mono">
                                                                    {failure.operationId}
                                                                </td>
                                                                <td className="err">
                                                                    {failure.errorCode}: {failure.errorMessage}
                                                                </td>
                                                                <td className="num">{failure.attempt}</td>
                                                                <td className="num muted">
                                                                    {fmtAgo(failure.ts)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>

                                    <div className="ph-tech__block">
                                        <div className="ph-tech__block-hd">
                                            <div className="ph-tech__block-title">
                                                Comprobación de cuadre
                                                {drift !== null && drift.length > 0 ? (
                                                    <span className="count">{drift.length}</span>
                                                ) : null}
                                            </div>
                                            <div className="ph-tech__block-meta">
                                                Firestore vs Elasticsearch por workspace · cada 4 h
                                            </div>
                                        </div>
                                        {drift === null || drift.length === 0 ? (
                                            <div className="ph-empty" data-sev="ok">
                                                <span className="material-symbols-outlined">
                                                    check_circle
                                                </span>
                                                <div>
                                                    <b>Sin desfases registrados</b>{" "}
                                                    <span>· los conteos cuadran con la realidad.</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="ph-scroll">
                                                <table className="ph-tbl">
                                                    <thead>
                                                        <tr>
                                                            <th>Dominio</th>
                                                            <th>Workspace</th>
                                                            <th className="num">Firestore</th>
                                                            <th className="num">Elasticsearch</th>
                                                            <th className="num">Desfase</th>
                                                            <th className="num">Cuándo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {drift.map((report) => (
                                                            <tr
                                                                key={`${report.runId}-${report.domain}-${report.workspaceId}`}
                                                            >
                                                                <td className="strong">{report.domain}</td>
                                                                <td className="mono">{report.workspaceId}</td>
                                                                <td className="num">
                                                                    {report.firestoreCount.toLocaleString()}
                                                                </td>
                                                                <td className="num">
                                                                    {report.esCount.toLocaleString()}
                                                                </td>
                                                                <td
                                                                    className={
                                                                        report.alert ? "num lag-crit" : "num"
                                                                    }
                                                                >
                                                                    {fmtPct(report.driftPct)}
                                                                </td>
                                                                <td className="num muted">
                                                                    {fmtTime(report.ts)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </>
                )}
            </div>
        </Shell>
    );
}
